<script setup>
// One part on the map, inside its area: illustration, name, one line, and an
// "Admin feature" tag when only club admins see or manage it. Positioned by the
// layout. At rest every part in the entered area is at full strength; hover or
// selection highlights by lift and outline, never by dimming the others. The
// only dimming is a search with no match here. Names wrap to two lines and
// descriptions to three (the card is tall enough for both), so neither ends in
// an ellipsis. Until the pointer moves after a fly-in nothing lifts (sm--hover-off).
// The admin tag's lock is a plain inline SVG (the mdi lock-outline path), not a
// v-icon: an area mounts up to twenty of these cards in one go, and the
// component-free tag keeps that task short (R48 L1).
import SystemMapArt from '~/components/systemMap/SystemMapArt.vue';
import {PART_LINE_PX, PART_NAME_PX} from '~/utils/systemMapLayout';

// The type sizes come from the layout module so the test can prove what they
// come to on screen at every area's entry view (never under 13px / 11px).
const typeVars = {'--part-name-px': `${PART_NAME_PX}px`, '--part-line-px': `${PART_LINE_PX}px`};

defineProps({
  node: {type: Object, required: true},
  pos: {type: Object, required: true},
  flags: {type: Object, required: true},
  dim: {type: Boolean, default: false},     // search only
  active: {type: Boolean, default: false},  // hovered or selected
  open: {type: Boolean, default: false},    // its dialog is open
  pulse: {type: Boolean, default: false},   // just flown to
  interactive: {type: Boolean, default: true},
});
const emit = defineEmits(['open', 'hover']);
</script>

<template>
  <div
      class="smn"
      :class="{'smn--dim': dim, 'smn--active': active, 'smn--open': open, 'smn--pulse': pulse, 'smn--admin': flags.admin, 'smn--root': pos.depth === 0}"
      :style="{left: `${pos.x}px`, top: `${pos.y}px`, width: `${pos.w}px`, height: `${pos.h}px`, ...typeVars}"
      :data-node="node.id"
  >
    <button
        type="button"
        class="smn-card"
        :tabindex="interactive ? 0 : -1"
        :aria-label="`${node.name}: ${node.line}`"
        @click="interactive && emit('open', node.id)"
        @mouseenter="interactive && emit('hover', node.id)"
        @mouseleave="interactive && emit('hover', null)"
        @focus="interactive && emit('hover', node.id)"
        @blur="interactive && emit('hover', null)"
    >
      <span class="smn-art-wrap"><system-map-art :name="node.art" :size="34" class="smn-art"/></span>
      <span class="smn-text">
        <span class="smn-name">{{ node.name }}</span>
        <span class="smn-line">{{ node.line }}</span>
      </span>
      <span v-if="flags.admin" class="smn-tag"><svg class="smn-tag-ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0-2 2 2 2 0 0 0 2 2m6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V6a5 5 0 0 1 5-5 5 5 0 0 1 5 5v2h1m-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3z"/></svg>Admin feature</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.smn {
  position: absolute;
  transition: opacity 180ms ease;

  &--dim {
    opacity: 0.22;
  }
}

.smn-card {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  text-align: left;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
  color: rgb(var(--v-theme-text));
  box-shadow: 0 1px 2px rgba(26, 26, 26, 0.04), 0 10px 24px -18px rgba(26, 26, 26, 0.25);
  transition: box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease;

  &:hover {
    border-color: rgb(var(--v-theme-border));
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(26, 26, 26, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }
}

/* Entering an area: the card that happens to be under a still pointer does not light up */
.sm--hover-off .smn-card:hover {
  transform: none;
  border-color: rgb(var(--v-theme-borderLight));
  box-shadow: 0 1px 2px rgba(26, 26, 26, 0.04), 0 10px 24px -18px rgba(26, 26, 26, 0.25);
}

.smn--root .smn-card {
  border-width: 1.5px;
  border-color: rgb(var(--v-theme-border));
}

.smn--active .smn-card,
.smn--open .smn-card {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 3px rgb(var(--v-theme-brandTint)), 0 10px 26px rgba(211, 41, 70, 0.14);
}

.smn--pulse .smn-card {
  animation: smn-pulse 1.4s ease-out 2;
}

@keyframes smn-pulse {
  0% { box-shadow: 0 0 0 0 rgba(211, 41, 70, 0.45); }
  100% { box-shadow: 0 0 0 22px rgba(211, 41, 70, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .smn--pulse .smn-card { animation: none; }
  .smn, .smn-card { transition: none; }
  .smn-card:hover { transform: none; }
}

.smn-art-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgb(var(--v-theme-light));
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.smn-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}

.smn-name {
  font-size: var(--part-name-px, 15px);
  font-weight: 800;
  letter-spacing: -0.2px;
  line-height: 1.2;
  /* Long names wrap onto a second line rather than ending in an ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.smn-line {
  font-size: var(--part-line-px, 12.5px);
  line-height: 1.3;
  color: rgb(var(--v-theme-charcoal));
  /* Three lines hold every description (the data keeps them under PART_LINE_MAX_CHARS) */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* The tag sits on the card's top edge so it never fights the two text lines */
.smn-tag {
  position: absolute;
  top: -9px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 999px;
  white-space: nowrap;
  color: rgb(var(--v-theme-charcoal));
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
}

.smn-tag-ic {
  width: 10px;
  height: 10px;
  fill: currentColor;
  flex: none;
}
</style>
